import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Image,
  Grid,
  GridItem,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  RadioGroup,
  Radio,
  Stack,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon
} from '@chakra-ui/react'
import { useRecoilValue, useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import { purchaseBasicPass, getUser, getPointShopStock, giftPoint } from '../services/apiService'

// 샘플 포인트샵 아이템 데이터
const sampleItems = [
  {
    id: 1,
    name: 'BASIC 이용권 (1개월)',
    description: 'BASIC 등급 이용권을 1개월간 이용할 수 있습니다.',
    price: 200,
    image: 'https://img.freepik.com/premium-photo/service-excellence-high-quality-service-concept-continuous-improvement-invest-training_43780-10347.jpg?semt=ais_incoming&w=740&q=80',
    category: 'subscription',
    stock: 100
  }
]

const PointShop: React.FC = () => {
  const { t } = useTranslation()
  const [auth, setAuth] = useRecoilState(authState)
  const toast = useToast()
  const [items, setItems] = useState(sampleItems)
  const [loading, setLoading] = useState(false)
  const [dailyStock, setDailyStock] = useState(5) // 일일 재고 (5개)
  const [stockInfo, setStockInfo] = useState({
    dailyLimit: 5,
    todaySoldCount: 0,
    remainingStock: 5,
    isAvailable: true
  })
  
  // 구매 모달 관련 상태
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [purchaseType, setPurchaseType] = useState<'self' | 'other'>('self')
  const [targetUsername, setTargetUsername] = useState('')
  const [purchaseLoading, setPurchaseLoading] = useState(false)

  // 포인트 선물 모달 관련 상태
  const { isOpen: isGiftOpen, onOpen: onGiftOpen, onClose: onGiftClose } = useDisclosure()
  const [giftTargetUsername, setGiftTargetUsername] = useState('')
  const [giftPointAmount, setGiftPointAmount] = useState('')
  const [giftLoading, setGiftLoading] = useState(false)

  // 재고 정보 로드 함수
  const loadStockInfo = async () => {
    try {
      const response = await getPointShopStock()
      if (response.statusCode === 200 && response.data) {
        setStockInfo(response.data)
        setDailyStock(response.data.remainingStock)
      }
    } catch (error) {
      console.error('재고 정보 로드 실패:', error)
    }
  }

  // 컴포넌트 마운트 시 재고 정보 로드
  useEffect(() => {
    loadStockInfo()
  }, [])

  const handlePurchaseClick = (item: any) => {
    if (!auth.id) {
      toast({
        title: '로그인이 필요합니다',
        description: '포인트샵을 이용하려면 로그인해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setSelectedItem(item)
    setPurchaseType('self')
    setTargetUsername('')
    onOpen()
  }

  const handleConfirmPurchase = async () => {
    if (!selectedItem) return

    setPurchaseLoading(true)
    
    try {
      const response = await purchaseBasicPass({
        purchaseType,
        targetUsername: purchaseType === 'other' ? targetUsername : undefined
      })

      if (response.data.statusCode === 200 && response.data.data) {
        const message = purchaseType === 'self' 
          ? `${selectedItem.name}을(를) 구매했습니다.`
          : `${targetUsername}님에게 ${selectedItem.name}을(를) 선물했습니다.`
        
        // 구매 완료 후 사용자 정보를 다시 가져와서 포인트 잔액 업데이트
        try {
          const { data: userResponse } = await getUser()
          if (userResponse.statusCode === 200 && userResponse.data) {
            setAuth(prevAuth => ({
              ...prevAuth,
              pointBalance: userResponse.data.pointBalance
            }))
          }
        } catch (userError) {
          console.error('사용자 정보 업데이트 실패:', userError)
          // 사용자 정보 업데이트 실패해도 구매는 성공했으므로 계속 진행
        }

        // 재고 정보 즉시 갱신
        await loadStockInfo()
        
        toast({
          title: '구매 완료',
          description: message,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        
        onClose()
        
        // 모달 닫힌 후 재고 정보 한 번 더 갱신 (확실하게)
        setTimeout(() => {
          loadStockInfo()
        }, 500)
      } else {
        throw new Error(response.data.message || '구매에 실패했습니다.')
      }
    } catch (error: any) {
      let errorMessage = '구매 중 오류가 발생했습니다.'
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast({
        title: '구매 실패',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setPurchaseLoading(false)
    }
  }

  const handleGiftPoint = async () => {
    if (!giftTargetUsername.trim() || !giftPointAmount.trim()) {
      toast({
        title: '입력 오류',
        description: '받는 사람 아이디와 포인트 금액을 모두 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    const pointAmount = parseFloat(giftPointAmount)
    if (isNaN(pointAmount) || pointAmount <= 0) {
      toast({
        title: '입력 오류',
        description: '올바른 포인트 금액을 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (pointAmount > parseFloat(auth.pointBalance || '0')) {
      toast({
        title: '포인트 부족',
        description: '보유 포인트가 부족합니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }

    setGiftLoading(true)
    
    try {
      const response = await giftPoint({
        targetUsername: giftTargetUsername.trim(),
        pointAmount: pointAmount
      })

      if (response.data.statusCode === 200 && response.data.data) {
        // 포인트 선물 완료 후 사용자 정보를 다시 가져와서 포인트 잔액 업데이트
        try {
          const { data: userResponse } = await getUser()
          if (userResponse.statusCode === 200 && userResponse.data) {
            setAuth(prevAuth => ({
              ...prevAuth,
              pointBalance: userResponse.data.pointBalance
            }))
          }
        } catch (userError) {
          console.error('사용자 정보 업데이트 실패:', userError)
        }

        toast({
          title: '포인트 선물 완료',
          description: `${giftTargetUsername}님에게 포인트 ${pointAmount}P를 성공적으로 선물했습니다.`,
          status: 'success',
          duration: 3000,
          isClosable: true
        })
        
        onGiftClose()
        setGiftTargetUsername('')
        setGiftPointAmount('')
      } else {
        throw new Error(response.data.message || '포인트 선물에 실패했습니다.')
      }
    } catch (error: any) {
      let errorMessage = '포인트 선물 중 오류가 발생했습니다.'
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast({
        title: '포인트 선물 실패',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setGiftLoading(false)
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'subscription': return 'purple'
      case 'points': return 'green'
      case 'service': return 'blue'
      default: return 'gray'
    }
  }

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'subscription': return '이용권'
      case 'service': return '서비스'
      default: return category
    }
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* 헤더 */}
          <Box textAlign="center">
            <Heading size="xl" mb={4} color="blue.500">
              🎁 포인트샵
            </Heading>
            <Text fontSize="lg" color="gray.600" mb={6}>
              포인트로 다양한 혜택을 구매하세요
            </Text>
            {auth.id && (
              <Box 
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
                borderRadius="xl" 
                p={6} 
                color="white"
                boxShadow="lg"
                mb={8}
              >
                <VStack spacing={4}>
                  <VStack spacing={2}>
                    <Text fontSize="sm" opacity={0.9}>현재 보유 포인트</Text>
                    <Text fontSize="3xl" fontWeight="bold">
                      {Math.floor(parseFloat(auth.pointBalance || '0'))}P
                    </Text>
                    <Text fontSize="xs" opacity={0.8}>
                      1P = 1USDT 동등 가치
                    </Text>
                  </VStack>
                  
                  <Button
                    colorScheme="pink"
                    size="md"
                    onClick={onGiftOpen}
                    bg="rgba(255, 255, 255, 0.2)"
                    _hover={{ bg: 'rgba(255, 255, 255, 0.3)' }}
                    color="white"
                    border="1px solid rgba(255, 255, 255, 0.3)"
                  >
                    🎁 포인트 선물하기
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>


          {/* 상품 목록 */}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
            {items.map(item => (
              <GridItem key={item.id}>
                <Card
                  maxW="sm"
                  mx="auto"
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                  transition="all 0.2s"
                >
                  <CardHeader pb={2}>
                    <HStack justify="space-between" align="start">
                      <Badge colorScheme={getCategoryColor(item.category)}>
                        {getCategoryLabel(item.category)}
                      </Badge>
                      <Text fontSize="sm" color="gray.500">
                        남은 재고: {stockInfo.remainingStock}개 / {stockInfo.dailyLimit}개
                      </Text>
                    </HStack>
                  </CardHeader>
                  
                  <CardBody pt={0}>
                    <VStack spacing={4} align="stretch">
                      <Image
                        src={item.image}
                        alt={item.name}
                        borderRadius="md"
                        height="150px"
                        objectFit="cover"
                      />
                      
                      <VStack spacing={2} align="stretch">
                        <Heading size="md" color="gray.800">
                          {item.name}
                        </Heading>
                        <Text fontSize="sm" color="gray.600" noOfLines={2}>
                          {item.description}
                        </Text>
                      </VStack>

                      <VStack spacing={3} align="stretch">
                        <HStack justify="space-between" align="center">
                          <VStack spacing={0} align="start">
                            <Text fontSize="lg" fontWeight="bold" color="blue.500">
                              {item.price}P
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              포인트
                            </Text>
                          </VStack>
                          
                          <Button
                            colorScheme="blue"
                            size="sm"
                            onClick={() => handlePurchaseClick(item)}
                            isDisabled={!auth.id || !stockInfo.isAvailable}
                          >
                            {!stockInfo.isAvailable ? '일일 한도 초과' : '구매하기'}
                          </Button>
                        </HStack>
                        
                        {auth.id && (
                          <Box 
                            bg={parseFloat(auth.pointBalance || '0') >= item.price ? 'green.50' : 'red.50'}
                            p={2} 
                            borderRadius="md" 
                            borderWidth="1px"
                            borderColor={parseFloat(auth.pointBalance || '0') >= item.price ? 'green.200' : 'red.200'}
                          >
                            <HStack justify="space-between" fontSize="sm">
                              <Text color="gray.600">보유 포인트:</Text>
                              <Text 
                                fontWeight="bold" 
                                color={parseFloat(auth.pointBalance || '0') >= item.price ? 'green.600' : 'red.600'}
                              >
                                {Math.floor(parseFloat(auth.pointBalance || '0'))}P
                              </Text>
                            </HStack>
                            {parseFloat(auth.pointBalance || '0') < item.price && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                부족: {item.price - Math.floor(parseFloat(auth.pointBalance || '0'))}P
                              </Text>
                            )}
                          </Box>
                        )}
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              </GridItem>
            ))}
          </Grid>

        </VStack>
      </Container>

      {/* 구매 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이용권 구매</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="gray.50" borderRadius="md">
                  <HStack spacing={4}>
                    <Image
                      src={selectedItem.image}
                      alt={selectedItem.name}
                      boxSize="60px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="bold">{selectedItem.name}</Text>
                      <Text fontSize="sm" color="gray.600">{selectedItem.description}</Text>
                      <Text fontSize="lg" fontWeight="bold" color="blue.500">
                        {selectedItem.price}P
                      </Text>
                    </VStack>
                  </HStack>
                </Box>

                <FormControl>
                  <FormLabel>구매 옵션</FormLabel>
                  <RadioGroup value={purchaseType} onChange={(value) => setPurchaseType(value as 'self' | 'other')}>
                    <Stack direction="column" spacing={2}>
                      <Radio value="self">본인에게 적용</Radio>
                      <Radio value="other">타인에게 선물</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {purchaseType === 'other' && (
                  <FormControl>
                    <FormLabel>받는 사람 아이디</FormLabel>
                    <Input
                      placeholder="아이디를 입력하세요"
                      value={targetUsername}
                      onChange={(e) => setTargetUsername(e.target.value)}
                    />
                  </FormControl>
                )}

                <Box 
                  bg="blue.50" 
                  p={3} 
                  borderRadius="md" 
                  borderWidth="1px" 
                  borderColor="blue.200"
                >
                  <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">현재 보유 포인트:</Text>
                      <Text fontSize="sm" fontWeight="bold" color="blue.600">
                        {Math.floor(parseFloat(auth.pointBalance || '0'))}P
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">구매 비용:</Text>
                      <Text fontSize="sm" fontWeight="bold" color="red.600">
                        -{selectedItem.price}P
                      </Text>
                    </HStack>
                    <HStack justify="space-between" borderTop="1px" borderColor="blue.200" pt={2}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.700">구매 후 잔액:</Text>
                      <Text fontSize="sm" fontWeight="bold" color={parseFloat(auth.pointBalance || '0') >= selectedItem.price ? 'green.600' : 'red.600'}>
                        {Math.floor(parseFloat(auth.pointBalance || '0')) - selectedItem.price}P
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <Text fontSize="sm">
                      {purchaseType === 'self' 
                        ? '본인의 BASIC 이용권 기간이 1개월 연장됩니다.'
                        : `${targetUsername}님의 BASIC 이용권 기간이 1개월 연장됩니다.`
                      }
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      남은 재고: {stockInfo.remainingStock}개 / {stockInfo.dailyLimit}개 (오늘 판매: {stockInfo.todaySoldCount}개)
                    </Text>
                  </Box>
                </Alert>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              취소
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmPurchase}
              isLoading={purchaseLoading}
              loadingText="구매중..."
              isDisabled={
                (purchaseType === 'other' && !targetUsername.trim()) ||
                (selectedItem && parseFloat(auth.pointBalance || '0') < selectedItem.price) ||
                !stockInfo.isAvailable
              }
            >
              {selectedItem && parseFloat(auth.pointBalance || '0') < selectedItem.price 
                ? '포인트 부족' 
                : !stockInfo.isAvailable 
                ? '일일 한도 초과'
                : '구매하기'
              }
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 포인트 선물 모달 */}
      <Modal isOpen={isGiftOpen} onClose={onGiftClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>포인트 선물하기</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="pink.50" borderRadius="md" borderWidth="1px" borderColor="pink.200">
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" fontWeight="bold" color="pink.700">
                    💝 포인트 선물 안내
                  </Text>
                  <Text fontSize="sm" color="pink.600">
                    • 보유 포인트를 다른 사용자에게 선물할 수 있습니다
                  </Text>
                  <Text fontSize="sm" color="pink.600">
                    • 선물한 포인트는 즉시 차감되며 되돌릴 수 없습니다
                  </Text>
                  <Text fontSize="sm" color="pink.600">
                    • 선물 내역은 히스토리에 기록됩니다
                  </Text>
                </VStack>
              </Box>

              <FormControl>
                <FormLabel>받는 사람 아이디</FormLabel>
                <Input
                  placeholder="아이디를 입력하세요"
                  value={giftTargetUsername}
                  onChange={(e) => setGiftTargetUsername(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>선물할 포인트 금액</FormLabel>
                <Input
                  type="number"
                  placeholder="포인트 금액을 입력하세요"
                  value={giftPointAmount}
                  onChange={(e) => setGiftPointAmount(e.target.value)}
                  min="1"
                  max={Math.floor(parseFloat(auth.pointBalance || '0'))}
                />
              </FormControl>

              <Box 
                bg="blue.50" 
                p={3} 
                borderRadius="md" 
                borderWidth="1px" 
                borderColor="blue.200"
              >
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">현재 보유 포인트:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="blue.600">
                      {Math.floor(parseFloat(auth.pointBalance || '0'))}P
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontSize="sm" color="gray.600">선물할 포인트:</Text>
                    <Text fontSize="sm" fontWeight="bold" color="red.600">
                      -{giftPointAmount || '0'}P
                    </Text>
                  </HStack>
                  <HStack justify="space-between" borderTop="1px" borderColor="blue.200" pt={2}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700">선물 후 잔액:</Text>
                    <Text fontSize="sm" fontWeight="bold" color={parseFloat(auth.pointBalance || '0') >= parseFloat(giftPointAmount || '0') ? 'green.600' : 'red.600'}>
                      {Math.floor(parseFloat(auth.pointBalance || '0')) - parseFloat(giftPointAmount || '0')}P
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <Text fontSize="sm" fontWeight="bold">
                    주의사항
                  </Text>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    포인트 선물은 되돌릴 수 없습니다. 받는 사람의 아이디를 정확히 확인해주세요.
                  </Text>
                </Box>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onGiftClose}>
              취소
            </Button>
            <Button
              colorScheme="pink"
              onClick={handleGiftPoint}
              isLoading={giftLoading}
              loadingText="선물중..."
              isDisabled={
                !giftTargetUsername.trim() ||
                !giftPointAmount.trim() ||
                parseFloat(giftPointAmount || '0') <= 0 ||
                parseFloat(auth.pointBalance || '0') < parseFloat(giftPointAmount || '0')
              }
            >
              포인트 선물하기
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default PointShop
